import { faBuildingColumns } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { twMerge } from 'tailwind-merge'
import { useCashier } from '../hooks/useCashier'

type CashierButtonVariant = 'icon' | 'pill'

/*
  The balance readout is also a button, but it cannot carry an action affordance without competing
  with the number it exists to show — which is why it ended up with `p-0`, no border and a hover
  underline, i.e. looking exactly like the status display it sits in. Players reported the balance as
  the last thing they would think to click, and that is a fair reading of it. This is the deliberate
  control; the balance stays clickable for anyone who already learned it.
*/
const variants: Record<CashierButtonVariant, { root: string; label: string }> = {
    /*
      Both bars: a bare bank glyph, no border and no word.

      It carried both at first. Jon's call after seeing it in place, and the layout agrees — beside
      the hamburger on mobile and the coin marks on desktop, everything else in these rows is an
      unboxed symbol, so a bordered box was the one framed element in the bar. The glyph is not
      ambiguous enough to need the label spelled out next to it.

      That leaves hover as the whole affordance, so it is the nav links' own `#C91FFF` rather than
      something invented here, plus `cursor-pointer` — the pair a pointing device reads as "button".
      `active:` repeats it for touch, where there is no hover at all.

      Dropping the word also retires a measured layout problem: labelled, this was 130px and pushed
      the nav groups onto a second line at 1280, taking the bar from 88px to 119px. At 44px there is
      no width at which it can do that.
    */
    icon: {
        root: 'flex h-11 w-11 shrink-0 items-center justify-center cursor-pointer text-white hover:text-[#C91FFF] active:text-[#C91FFF]',
        label: 'hidden',
    },
    /*
      Mobile drawer, where every sibling is a full-width labelled pill on the gradient — the
      Dashboard link directly beneath it is the same shape. Keeps its word and its background for
      that reason: a bare glyph in a column of pills would be the odd one out, which is the same
      argument that stripped the box off the bars.
    */
    pill: {
        root: 'flex min-h-[44px] w-full flex-row items-center justify-center gap-2 rounded-3xl bg-black/80 px-4 text-base tracking-[0.15em] shadow-lg',
        label: 'inline',
    },
}

/**
 * The deliberate way into deposit/withdraw. Opens on the deposit tab because that is the direction
 * new money travels; the modal's own toggle covers the other one, so this stays a single control
 * instead of splitting the bar into two.
 *
 * `onNavigate` exists for the drawer: the modal locks page scroll, and leaving the drawer open
 * behind it means closing the modal drops you back into a menu you had finished with.
 */
export const CashierButton = ({
    variant = 'icon',
    className,
    onNavigate,
}: {
    variant?: CashierButtonVariant
    className?: string
    onNavigate?: () => void
}) => {
    const { open } = useCashier()

    return (
        <button
            onClick={() => {
                onNavigate?.()
                open('deposit')
            }}
            title='Deposit or withdraw funds'
            aria-label='Cashier: deposit or withdraw funds'
            className={twMerge(
                'font-bold uppercase whitespace-nowrap transition-colors',
                variants[variant].root,
                className
            )}
        >
            {/*
              A bank, as asked for. Where the word does appear it is "Cashier" rather than "Wallet":
              `Wallets` already means connected Solana wallets on the dashboard and the account page,
              and reusing it would make one label mean two things.

              The span stays in the tree even when hidden so the two variants differ by class alone.
              `aria-label` above is what assistive tech reads either way, so the bars are not silent
              once the visible word is gone.
            */}
            <FontAwesomeIcon icon={faBuildingColumns} className={variant === 'icon' ? 'text-2xl' : undefined} />
            <span className={variants[variant].label}>Cashier</span>
        </button>
    )
}
